import { LayoutDashboard, Users, FileText, CheckSquare, LogOut, Settings, Layers, X, ClipboardList, DollarSign, Bell, Crown, ListChecks } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FeedbackButton } from './FeedbackButton';
import { FloatingNote } from './FloatingNote';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [customTabName, setCustomTabName] = useState('Onboarding');
  const [badges, setBadges] = useState({ reminders: 0, forms: 0 });

  const fetchBadges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sidebar-badges`, { withCredentials: true });
      setBadges(data);
    } catch {}
  }, []);

  useEffect(() => {
    const handler = () => setMobileOpen(prev => !prev);
    window.addEventListener('toggle-sidebar', handler);
    return () => window.removeEventListener('toggle-sidebar', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (user) {
      fetchBadges();
      const interval = setInterval(fetchBadges, 60000);
      const onRefresh = () => fetchBadges();
      window.addEventListener('sidebar-badges:refresh', onRefresh);
      return () => {
        clearInterval(interval);
        window.removeEventListener('sidebar-badges:refresh', onRefresh);
      };
    }
  }, [user, fetchBadges]);

  useEffect(() => {
    const fetchTabName = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/onboarding/steps`, { withCredentials: true });
        if (data.tab_name) setCustomTabName(data.tab_name);
      } catch {}
    };
    if (user) fetchTabName();
  }, [user]);

  const isManager = user?.role === 'manager';

  const allNavItems = [
    { name: 'Pipeline', path: '/dashboard', icon: LayoutDashboard },
    { name: 'All Deals', path: '/all-deals', icon: Layers },
    { name: 'Tasks', path: '/tasks', icon: CheckSquare, badgeKey: 'reminders' },
    { name: 'Contacts', path: '/partners', icon: Users, repOnly: true },
    { name: 'Quick Notes', path: '/quick-notes', icon: FileText },
    { name: 'Commissions', path: '/commissions', icon: DollarSign, repOnly: true },
    { name: customTabName, path: '/onboarding', icon: ClipboardList, repOnly: true },
    { name: 'Deal Forms', path: '/forms', icon: ListChecks, badgeKey: 'forms', managerOnly: true },
    { name: 'Settings', path: '/settings', icon: Settings },
    { name: 'Creator', path: '/creator', icon: Crown, creatorOnly: true },
  ];

  const navItems = allNavItems.filter(i => {
    if (i.creatorOnly && user?.role !== 'creator') return false;
    if (i.repOnly && isManager) return false;
    if (i.managerOnly && !isManager) return false;
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navContent = (
    <>
      <div className="p-5 md:p-6 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-black tracking-tighter text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            deal <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">desk</span>
          </h1>
          <p className="text-xs text-slate-400 font-medium">manage your deals</p>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="md:hidden p-1 text-slate-400 hover:text-white"
        >
          <X size={20} />
        </button>
      </div>
      
      <nav className="flex-1 p-3 md:p-4 space-y-1.5 md:space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const badgeCount = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
          const isActive = location.pathname === item.path || 
            (item.path === '/dashboard' && location.pathname.startsWith('/pipeline/')) ||
            (item.path === '/all-deals' && location.pathname.startsWith('/account/'));
          return (
            <button
              key={item.path}
              data-testid={`sidebar-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => navigate(item.path)}
              className={isActive
                ? "flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold shadow-lg shadow-indigo-500/30 w-full text-left transition-all"
                : "flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all cursor-pointer w-full text-left"}
            >
              <Icon size={20} strokeWidth={2} />
              <span className="font-medium flex-1">{item.name}</span>
              {badgeCount > 0 && (
                <span
                  data-testid={`sidebar-badge-${item.badgeKey}`}
                  className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5 animate-in fade-in"
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      
      <div className="p-3 md:p-4 border-t border-slate-800">
        <div className="flex items-center justify-center mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 bg-slate-800/50 px-3 py-1 rounded-full">v1.0</span>
        </div>
        <button
          data-testid="sidebar-logout-button"
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all cursor-pointer w-full text-left"
        >
          <LogOut size={20} strokeWidth={2} />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex w-64 bg-gradient-to-b from-slate-950 to-slate-900 flex-col h-screen fixed left-0 top-0 shadow-2xl z-50">
        {navContent}
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <div
        className={`md:hidden fixed top-0 left-0 h-screen w-72 bg-gradient-to-b from-slate-950 to-slate-900 flex flex-col z-50 shadow-2xl transform transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {navContent}
      </div>

      {/* Floating Feedback Button */}
      <FloatingNote />
      <FeedbackButton />
    </>
  );
};
